import { useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { Input } from '@/components/forms';
import { Button } from '@/components/ui';
import type { RenderWizardFormData } from '@/types/wizard';
import styles from './RenderDetailsStep.module.css';

const NUM_OPTIONS = [4, 6, 8, 10] as const;
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

interface Props {
  formData: RenderWizardFormData;
  updateForm: <K extends keyof RenderWizardFormData>(
    key: K,
    value: RenderWizardFormData[K],
  ) => void;
  onNext: () => void;
  onBack: () => void;
}

export const RenderDetailsStep = ({ formData, updateForm, onNext, onBack }: Props) => {
  const canProceed = Boolean(formData.event_name.trim());
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = useCallback(
    async (file: File) => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return;
      setIsUploadingLogo(true);
      try {
        const form = new FormData();
        form.append('files', file);
        const res = await api.post(API_ROUTES.uploadProviderImage, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const uploaded = res.data?.uploaded as Array<{ url: string }> | undefined;
        if (uploaded && uploaded.length > 0) {
          updateForm('logo_url', uploaded[0].url);
        }
      } catch {
        // silent
      } finally {
        setIsUploadingLogo(false);
      }
    },
    [updateForm],
  );

  const handleLogoDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = Array.from(e.dataTransfer.files).find((f) =>
        ACCEPTED_IMAGE_TYPES.includes(f.type),
      );
      if (file) handleLogoUpload(file);
    },
    [handleLogoUpload],
  );

  return (
    <div className={styles.container}>
      <button className={styles.backButton} onClick={onBack}>
        <FontAwesomeIcon icon={faArrowLeft} className={styles.backArrow} /> Back
      </button>

      <h2 className={styles.title}>Render Details</h2>
      <p className={styles.subtitle}>
        Describe the atmosphere and style for your event venue renders.
      </p>

      <div className={styles.form}>
        <div className={styles.row}>
          <Input
            name="event_name"
            label="Event Name *"
            value={formData.event_name}
            setValue={(v) => updateForm('event_name', v)}
            placeholder="e.g. Gala de Fin de Ano 2026"
          />

          <div className={styles.numSection}>
            <label className={styles.numLabel}>Number of Renders</label>
            <div className={styles.numButtons}>
              {NUM_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`${styles.numBtn} ${formData.num_renders === n ? styles.numBtnActive : ''}`}
                  onClick={() => updateForm('num_renders', n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Logo upload */}
        <div className={styles.logoSection}>
          <label className={styles.logoLabel}>Client / Company Logo</label>
          <p className={styles.logoHint}>
            Upload the client's logo to include it in the rendered images.
          </p>

          {formData.logo_url ? (
            <div className={styles.logoPreview}>
              <img src={formData.logo_url} alt="Logo" className={styles.logoImg} />
              <button
                type="button"
                className={styles.logoRemove}
                onClick={() => updateForm('logo_url', '')}
                aria-label="Remove logo"
              >
                &times;
              </button>
            </div>
          ) : (
            <div
              className={`${styles.logoDropzone} ${isUploadingLogo ? styles.logoUploading : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleLogoDrop}
              onClick={() => !isUploadingLogo && logoInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') logoInputRef.current?.click();
              }}
              aria-label="Upload logo"
            >
              <input
                ref={logoInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                className={styles.hiddenInput}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleLogoUpload(file);
                  if (e.target) e.target.value = '';
                }}
                disabled={isUploadingLogo}
              />
              {isUploadingLogo ? (
                <span className={styles.logoDropzoneText}>Uploading...</span>
              ) : (
                <>
                  <span className={styles.logoDropzoneIcon}>&#127912;</span>
                  <span className={styles.logoDropzoneText}>
                    Drag your logo here or <strong>click to select</strong>
                  </span>
                  <span className={styles.logoDropzoneHintText}>PNG, JPG, WebP, or SVG</span>
                </>
              )}
            </div>
          )}
        </div>

        <Input
          name="prompt"
          label="Describe your vision"
          value={formData.prompt}
          setValue={(v) => updateForm('prompt', v)}
          placeholder="Describe the atmosphere, style, and specific elements you want to see in the venue renders..."
          multiline
          rows={6}
        />
      </div>

      <div className={styles.actions}>
        {!canProceed && <p className={styles.hint}>Enter an event name to continue</p>}
        <Button
          variant="primary"
          size="lg"
          onClick={onNext}
          disabled={!canProceed || isUploadingLogo}
        >
          Generate Renders &rarr;
        </Button>
      </div>
    </div>
  );
};
